// Read-only pre-approval letter list for broker + borrower.
// Shows letters generated by admin; allows downloading the PDF.
import React, { useEffect, useState } from "react";
import { api, errorMessage, getToken } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Award, Download, FileText } from "lucide-react";
import { fmtCurrency, fmtDateTime } from "../lib/constants";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PreApprovalReadOnly({ applicationId }) {
  const [letters, setLetters] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/documents/application/${applicationId}/pre-approval`);
        if (!cancelled) setLetters(data);
      } catch (e) {
        if (!cancelled) toast.error(errorMessage(e));
      }
    })();
    return () => { cancelled = true; };
  }, [applicationId]);

  const download = (l) => {
    const token = getToken();
    fetch(`${BACKEND}/api/documents/pre-approval/${l.letter_id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!r.ok) throw new Error("Download failed"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = l.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((err) => toast.error(err.message || "Download failed"));
  };

  if (!letters.length) return null;

  return (
    <Card className="border-emerald-500/20 bg-emerald-500/[0.04] p-5" data-testid="pa-readonly-panel">
      <div className="mb-3 flex items-center gap-2">
        <Award className="h-4 w-4 text-emerald-300" />
        <div className="text-sm font-semibold text-emerald-200">Pre-Approval Letters</div>
      </div>
      <ul className="space-y-2">
        {letters.map((l) => (
          <li
            key={l.letter_id}
            className="flex items-center gap-3 rounded-md border border-slate-800 bg-slate-950 p-3"
            data-testid={`pa-ro-letter-${l.letter_id}`}
          >
            <FileText className="h-4 w-4 text-emerald-400" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-slate-100">
                {l.borrower_or_entity_name}{" "}
                <span className="text-slate-400">· up to {fmtCurrency(l.loan_amount)}</span>
              </div>
              <div className="text-[11px] text-slate-500">{fmtDateTime(l.created_at)}</div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => download(l)}
              className="border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800"
              data-testid={`pa-ro-download-${l.letter_id}`}
            >
              <Download className="mr-1.5 h-3.5 w-3.5" /> Download
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
